import {
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { Refresh } from '@material-ui/icons';
import { PublicKey, SYSVAR_RECENT_BLOCKHASHES_PUBKEY } from '@solana/web3.js';
import { useWallet } from '@solana/wallet-adapter-react';
import { FC, useCallback, useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router';
import Screen from '../../../components/layout/Screen';
import Spacer from '../../../components/Spacer';
import { useProgramApis } from '../../../hooks/useProgramApis';
import { useRafflesStore } from '../../../hooks/useRafflesStore';
import { collectProceeds } from '../../../lib/actions/collectProceeds';
import { closeRaffle } from '../../../lib/actions/closeRaffle';
import { txHandler } from '../../../lib/anchorUtils';
import { Raffle } from '../../../lib/types';
import { useStyles } from './styles';
import ClaimButton from '../../../components/ClaimButton';
import { expand } from '../../../lib/randomnessTools';
import toast from 'react-hot-toast';
import { claimPrize as claimPrizeQuery } from '../../../lib/actions/claimPrize';
import { useHistory } from 'react-router';
import { routes } from '../../../router/routes';
const AdminRaffleScreen: FC = () => {
  const classes = useStyles();
  const { connected, publicKey } = useWallet();
  const { id: raffleId } = useParams<{ id: string }>();
  const { raffles, updateRaffleById } = useRafflesStore();
  const { draffleClient } = useProgramApis();
  const { push } = useHistory();

  const [currentRaffle, setCurrentRaffle] = useState<Raffle>();
  const [loading, setLoading] = useState<boolean>(false);

  const [winnerKey, setWinnerKey] = useState(
    draffleClient.provider?.wallet?.publicKey?.toBase58() || ''
  );

  // Each winning ticket index for each prize
  const winningTickets = useMemo(() => {
    const raffle = currentRaffle;
    if (
      !raffle ||
      !raffle.randomness ||
      !raffle.entrants ||
      raffle.entrants.size === 0
    )
      return [];
    const secret = raffle.randomness;
    return raffle.prizes.map((_, prizeIndex) => {
      const rand = expand(secret, prizeIndex);
      return rand % raffle.totalTickets;
    });
  }, [currentRaffle]);

  useEffect(() => {
    const raffle = raffles.get(raffleId);
    if (raffle) setCurrentRaffle(raffle);
  }, [raffles, raffleId]);

  const revealWinners = useCallback(async () => {
    await txHandler(
      () =>
        draffleClient.rpc.revealWinners({
          accounts: {
            raffle: raffleId,
            recentBlockhashes: SYSVAR_RECENT_BLOCKHASHES_PUBKEY,
          },
        }),
      'Raffle revealed'
    ).then((res) => updateRaffleById(raffleId));
  }, [draffleClient, raffleId]);

  const ticketIndex = winningTickets[0];

  useEffect(() => {
    // console.log(ticketIndex, 'winning ticket');
    // console.log(raffle.entrants, 'raffle');
    if (currentRaffle && ticketIndex)
      Array.from(currentRaffle.entrants.values()).map((item: any) => {
        // console.log(item.tickets, 'ITEM')
        if (item.tickets.includes(ticketIndex))
          setWinnerKey(item.publicKey.toBase58());
      });
  }, [currentRaffle, ticketIndex]);

  const claimTokens = useCallback(async () => {
    if (currentRaffle === undefined) return;
    await txHandler(
      () =>
        collectProceeds(
          draffleClient,
          draffleClient.provider.wallet.publicKey,
          currentRaffle.proceeds.address,
          currentRaffle
        ),
      'You have successfully claimed the raffle ticket tokens'
    ).then((res) => updateRaffleById(raffleId));
  }, [currentRaffle, draffleClient]);

  const closeThisRaffle = useCallback(async () => {
    if (currentRaffle === undefined) return;
    await txHandler(
      () =>
        closeRaffle(
          draffleClient,
          draffleClient.provider.wallet.publicKey,
          currentRaffle.proceeds.address,
          currentRaffle
        ),
      'You have successfully closed this raffle'
    ).then((res) => push(`/dashboard`));
  }, [currentRaffle, draffleClient]);

  const claimPrize = useCallback(
    async (prizeIndex: number, ticketIndex: number) => {
      if (currentRaffle)
        try {
          await claimPrizeQuery(
            draffleClient,
            currentRaffle,
            prizeIndex,
            ticketIndex,
            winnerKey
              ? new PublicKey(winnerKey)
              : draffleClient.provider.wallet.publicKey
          );
          toast.success('Prize claimed, check your wallet!');
        } catch (error: any) {
          if (error.msg) {
            toast.error(`Transaction failed: ${error.msg}`);
          } else {
            toast.error('Unexpected error');
          }
        }
    },
    [draffleClient, currentRaffle]
  );

  console.log(raffleId, 'id');
  console.log(currentRaffle, 'cr');

  if (loading) {
    return (
      <div className={classes.root}>
        <Typography variant="h3">Loading...</Typography>
      </div>
    );
  }

  if (!currentRaffle) return null;

  const createData = (wallet: string, tickets: string) => {
    return { wallet, tickets };
  };

  const generateRows = () => {
    let newRows = [...currentRaffle.entrants.entries()].map(
      ([wallet, tickets]) => createData(wallet, tickets.tickets.toString())
    );
    return newRows;
  };

  const rows = generateRows();

  return (
    <div className={classes.root}>
      <div className="flex justify-center items-center">
        {currentRaffle.prizes[0]?.meta?.imageUri ? (
          <img
            width="200px"
            height="200px"
            src={currentRaffle.prizes[0]?.meta?.imageUri}
            alt={currentRaffle.prizes[0]?.meta?.name}
          />
        ) : (
          <div
            style={{ width: '200px', height: '200px' }}
            className="text-center bg-gray-500 flex items-center justify-center"
          >
            Invalid
          </div>
        )}
      </div>
      {currentRaffle ? (
        <>
          <div className="text-center py-6">
            {winnerKey && <p>Winner is: {winnerKey}</p>}
          </div>
          <div className={classes.headerContainer}>
            <p className="text-white ">
              Entrants list (total {currentRaffle.entrants.size})
            </p>
            <IconButton
              onClick={() => {
                setLoading(true);
                updateRaffleById(raffleId);
                setLoading(false);
              }}
            >
              <Refresh className="text-white" />
            </IconButton>
          </div>
          {connected && publicKey && currentRaffle.isEnded && (
            // currentRaffle.creator.toBase58() === publicKey.toBase58() &&
            <div>
              {currentRaffle.isEnded && !currentRaffle.randomness && (
                <Button onClick={revealWinners} variant="contained">
                  Reveal winners
                </Button>
              )}
              {/* {console.log(currentRaffle)} */}
              {!currentRaffle.claimedPrizes && currentRaffle.isEnded && (
                <div className="py-3">
                  <ClaimButton
                    claimPrize={claimPrize}
                    prize={currentRaffle.prizes[0]}
                    prizeIndex={0}
                    ticketIndex={ticketIndex}
                  />
                </div>
              )}
              {(!currentRaffle.proceedsCollected ||
                currentRaffle?.entrants?.size > 0) &&
                currentRaffle.claimedPrizes && (
                  <Button
                    style={{ marginLeft: 5 }}
                    onClick={claimTokens}
                    variant="contained"
                  >
                    Claim tokens
                  </Button>
                )}
              {currentRaffle.isEnded &&
              currentRaffle.claimedPrizes &&
              !currentRaffle?.entrants?.size ? (
                <Button
                  style={{ marginLeft: 5 }}
                  onClick={closeThisRaffle}
                  variant="contained"
                >
                  Close raffle
                </Button>
              ) : (
                <></>
              )}
            </div>
          )}
          <Spacer height={'20px'} />
          {currentRaffle.entrants.size > 0 ? (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Entry Wallet</TableCell>
                    <TableCell align="right">Ticket Number(s)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.wallet}>
                      <TableCell component="th" scope="row">
                        {row.wallet}
                      </TableCell>
                      <TableCell
                        align="right"
                        style={{ wordBreak: 'break-word', width: '33%' }}
                      >
                        {row.tickets}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="h3">
              No Participants / Raffle Closed
            </Typography>
          )}
        </>
      ) : (
        <Typography variant="h3">No entrants</Typography>
      )}
      <Spacer height={'50px'} />
    </div>
  );
};

const AdminRaffleScreenWithLayout = () => (
  <Screen>
    <AdminRaffleScreen />
  </Screen>
);

export default AdminRaffleScreenWithLayout;
